// src/components/FormManager.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Card, 
  Button, 
  Input, 
  Spin, 
  message, 
  notification, 
  Space, 
  Tag,
  Row,
  Col,
  Typography,
  Empty,
  Select,
  Badge,
  Divider,
  Tooltip,
  Modal,
  Table,
  Pagination
} from "antd";
import { 
  SearchOutlined, 
  FilterOutlined, 
  ReloadOutlined,
  EyeOutlined,
  DatabaseOutlined,
  ApartmentOutlined,
  EnvironmentOutlined,
  StarOutlined,
  CloseOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  LeftOutlined,
  RightOutlined,
  NumberOutlined,
  CalculatorOutlined,
  PercentageOutlined,
  FieldNumberOutlined,
  BarChartOutlined
} from "@ant-design/icons";

// Import the FormSelectionView component
import FormSelectionView from "./formSelectionView";

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

// ==================== API CONFIGURATION ====================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "http://localhost:5000";

// ==================== HELPER FUNCTIONS ====================

// 1. CHECK WORKFLOW COLUMN IN DATA
const checkWorkflowColumnInData = (data) => {
  if (!data || data.length === 0) return false;
  return 'workflow_action' in data[0];
};

// 2. GET APPROVED SUBMISSIONS ONLY
const getApprovedSubmissionsOnly = (formId, data) => {
  try {
    const hasWorkflowColumn = data.length > 0 && 'workflow_action' in data[0];

    if (!hasWorkflowColumn) {
      console.log(`📊 Form ${formId}: No workflow_action column found in data`);
      return [];
    }

    const approvedData = data.filter(row => {
      const action = row.workflow_action;
      return action === 'approve' ||
        action === 'approved' ||
        action === 'APPROVED' ||
        action === 'Approve' ||
        action === 'APPROVE';
    });

    console.log(`📊 Form ${formId}: ${approvedData.length} approved submissions (from ${data.length} total)`);
    return approvedData;
  } catch (error) {
    console.error(`Error filtering approved submissions for form ${formId}:`, error);
    return [];
  }
};

// Resolve IDs helper
const resolveIdsInData = (data, referenceCache = {}) => {
  if (!data || !Array.isArray(data)) return data;
  return data.map(row => {
    const resolved = { ...row };
    if (row.district_name) resolved.district = row.district_name;
    if (row.division_name) resolved.division = row.division_name;
    if (row.ulb_name) resolved.ulb = row.ulb_name;
    
    if (referenceCache.districts && row.district_id) {
      resolved.district_name = referenceCache.districts[row.district_id]?.name || row.district_id;
    }
    if (referenceCache.divisions && row.division_id) {
      resolved.division_name = referenceCache.divisions[row.division_id]?.name || row.division_id;
    }
    if (referenceCache.ulbs && row.ulb_id) {
      resolved.ulb_name = referenceCache.ulbs[row.ulb_id]?.ulb_name || row.ulb_id;
    }
    return resolved;
  });
};

// Format field name for display
const formatFieldName = (fieldName) => {
  if (!fieldName) return "Unknown Field";
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Get operator icon
const getOperatorIcon = (operator) => {
  const icons = {
    sum: <NumberOutlined />,
    count: <NumberOutlined />,
    avg: <CalculatorOutlined />,
    average: <CalculatorOutlined />,
    percentage: <PercentageOutlined />,
    max: <FieldNumberOutlined />,
    min: <FieldNumberOutlined />,
  };
  return icons[operator?.toLowerCase()] || <BarChartOutlined />;
};

// Format value locally
const formatValueLocal = (value, operator) => {
  if (value === undefined || value === null) return "0";
  if (operator?.toLowerCase() === "percentage") return `${value.toFixed(2)}%`;
  if (operator?.toLowerCase() === "avg" || operator?.toLowerCase() === "average") return value.toFixed(2);
  if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return value.toString();
};

// ==================== COLORS FOR CARDS ====================
const startColors = [
  '#1a6b8a', '#c0392b', '#4c1d95', '#14532d', '#9d174d', '#1e3a5f',
  '#92400e', '#1e293b', '#be185d', '#0e7490', '#3b0764', '#365314',
  '#7c2d12', '#1e1b4b', '#134e4a', '#6b21a8',
];

const endColors = [
  '#0d9488', '#e67e22', '#7c3aed', '#16a34a', '#db2777', '#2563eb',
  '#d97706', '#475569', '#f97316', '#38bdf8', '#a21caf', '#65a30d',
  '#c2410c', '#4338ca', '#0d9488', '#9d174d',
];

const hoverColors = [
  '#2a8b9a', '#d0493b', '#5c2da5', '#256342', '#ad2757', '#2e4a6f',
  '#a2571e', '#2e394b', '#ce286d', '#1e84a0', '#4b1874', '#466324',
  '#8c421c', '#2e2b5b', '#235e5a', '#7b31b8',
];

// ==================== FRONT PAGE FILTER SECTION ====================
const FrontPageFilterSection = ({ 
  frontPageFilters, 
  onFilterChange, 
  onApply, 
  onReset,
  filterOptions,
  loadingFilters,
  hasFullAccessFn,
  userRole,
  userDivisionId,
  userDistrictId,
  userUlbId,
  referenceCache
}) => {
  // Get user's location names
  const getUserDivisionName = () => {
    if (userDivisionId && referenceCache?.divisions?.[userDivisionId]) {
      return referenceCache.divisions[userDivisionId].name;
    }
    return null;
  };

  const getUserDistrictName = () => {
    if (userDistrictId && referenceCache?.districts?.[userDistrictId]) {
      return referenceCache.districts[userDistrictId].name;
    }
    return null;
  };

  const getUserUlbName = () => {
    if (userUlbId && referenceCache?.ulbs?.[userUlbId]) {
      return referenceCache.ulbs[userUlbId].ulb_name;
    }
    return null;
  };

  const isDivisionAdmin = !hasFullAccessFn?.() && (
    userRole === "DIVISION_ADMIN" || userRole === "division_admin" ||
    userRole === "DIVISIONAL_OFFICER" || userRole === "divisional_officer" ||
    userRole === "Divisionalcommissioner"
  );

  const isDistrictAdmin = !hasFullAccessFn?.() && (
    userRole === "DISTRICT_ADMIN" || userRole === "district_admin"
  );

  const isUlbAdmin = !hasFullAccessFn?.() && (
    userRole === "ULB_ADMIN" || userRole === "ulb_admin" ||
    userRole === "ULB_OFFICER" || userRole === "ULB_CHIEF_OFFICIER"
  );

  // Get the display value for division (user's division if admin)
  const displayDivisionValue = isDivisionAdmin ? getUserDivisionName() : frontPageFilters.division;
  
  // Get the display value for district (user's district if admin)
  const displayDistrictValue = isDistrictAdmin ? getUserDistrictName() : frontPageFilters.district;
  
  // Get the display value for ULB (user's ULB if admin)
  const displayUlbValue = isUlbAdmin ? getUserUlbName() : frontPageFilters.ulb_name;

  const isFilterDisabled = (filterType) => {
    if (hasFullAccessFn && hasFullAccessFn()) return false;

    if (filterType === "division") {
      return isDivisionAdmin || isDistrictAdmin || isUlbAdmin;
    }
    if (filterType === "district") {
      return isDistrictAdmin || isUlbAdmin;
    }
    if (filterType === "ulb") {
      return isUlbAdmin;
    }
    return false;
  };

  const getDivisionPlaceholder = () => {
    if (isDivisionAdmin) return `Division: ${getUserDivisionName() || "Loading..."}`;
    if (isDistrictAdmin) return "Division (Auto-filtered)";
    if (isUlbAdmin) return "Division (Auto-filtered)";
    return "All Divisions";
  };

  const getDistrictPlaceholder = () => {
    if (isDistrictAdmin) return `District: ${getUserDistrictName() || "Loading..."}`;
    if (isUlbAdmin) return `District: ${getUserDistrictName() || "Auto-filtered"}`;
    if (frontPageFilters.division) return "Select District";
    return "District (Select Division first)";
  };

  const getUlbPlaceholder = () => {
    if (isUlbAdmin) return `ULB: ${getUserUlbName() || "Loading..."}`;
    if (frontPageFilters.district) return "Select ULB";
    if (frontPageFilters.division) return "Select District first";
    return "Select Division/District first";
  };

  const hasActiveFilters = frontPageFilters.division || frontPageFilters.district || 
    frontPageFilters.ulb_type || frontPageFilters.ulb_class || frontPageFilters.ulb_name ||
    isDivisionAdmin || isDistrictAdmin || isUlbAdmin;

  return (
    <Card
      style={{
        marginBottom: 24,
        width: "100%",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        border: "none",
        borderRadius: "16px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        overflow: "hidden",
      }}
      bodyStyle={{ padding: "20px 24px", background: "transparent" }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
        {/* ULB Type Filter */}
        <div style={{ flex: "1 1 180px", minWidth: 160 }}>
          <div style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <ApartmentOutlined style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }} />
            <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: 12, fontWeight: 500 }}>ULB Type</Text>
          </div>
          <Select
            placeholder="All Types"
            value={frontPageFilters.ulb_type}
            onChange={(value) => onFilterChange('ulb_type', value)}
            allowClear={!isFilterDisabled("ulb_type")}
            style={{ width: "100%" }}
            loading={loadingFilters}
            showSearch
            disabled={isFilterDisabled("ulb_type")}
            size="middle"
            dropdownStyle={{ borderRadius: 12 }}
          >
            <Option value="">All Types</Option>
            {filterOptions.ulbTypes?.map((value) => (
              <Option key={value} value={value}>{value}</Option>
            ))}
          </Select>
        </div>

        {/* ULB Class Filter */}
        <div style={{ flex: "1 1 180px", minWidth: 160 }}>
          <div style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <StarOutlined style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }} />
            <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: 12, fontWeight: 500 }}>ULB Class</Text>
          </div>
          <Select
            placeholder="All Classes"
            value={frontPageFilters.ulb_class}
            onChange={(value) => onFilterChange('ulb_class', value)}
            allowClear={true}
            style={{ width: "100%" }}
            loading={loadingFilters}
            showSearch
            size="middle"
            dropdownStyle={{ borderRadius: 12 }}
          >
            <Option value="">All Classes</Option>
            {filterOptions.ulbClasses?.map((value) => (
              <Option key={value} value={value}>{value}</Option>
            ))}
          </Select>
        </div>

        {/* Division Filter */}
        <div style={{ flex: "1 1 200px", minWidth: 180 }}>
          <div style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <ApartmentOutlined style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }} />
            <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: 12, fontWeight: 500 }}>Division</Text>
          </div>
          <Select
            placeholder={getDivisionPlaceholder()}
            value={displayDivisionValue}
            onChange={(value) => onFilterChange('division', value)}
            allowClear={!isFilterDisabled("division") && !isDivisionAdmin}
            style={{ width: "100%" }}
            loading={loadingFilters}
            showSearch
            disabled={isFilterDisabled("division")}
            size="middle"
            dropdownStyle={{ borderRadius: 12 }}
          >
            <Option value="">All Divisions</Option>
            {filterOptions.divisions?.map((value) => (
              <Option key={value} value={value}>{value}</Option>
            ))}
          </Select>
        </div>

        {/* District Filter */}
        <div style={{ flex: "1 1 200px", minWidth: 180 }}>
          <div style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <EnvironmentOutlined style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }} />
            <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: 12, fontWeight: 500 }}>District</Text>
          </div>
          <Select
            placeholder={getDistrictPlaceholder()}
            value={displayDistrictValue}
            onChange={(value) => onFilterChange('district', value)}
            allowClear={!isFilterDisabled("district") && !isDistrictAdmin}
            style={{ width: "100%" }}
            loading={loadingFilters}
            showSearch
            disabled={isFilterDisabled("district") || (!displayDivisionValue && !hasFullAccessFn?.() && !isDistrictAdmin)}
            size="middle"
            dropdownStyle={{ borderRadius: 12 }}
          >
            <Option value="">All Districts</Option>
            {filterOptions.districts?.map((value) => (
              <Option key={value} value={value}>{value}</Option>
            ))}
          </Select>
        </div>

        {/* ULB Filter */}
        <div style={{ flex: "1 1 220px", minWidth: 200 }}>
          <div style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <DatabaseOutlined style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }} />
            <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: 12, fontWeight: 500 }}>ULB Name</Text>
          </div>
          <Select
            placeholder={getUlbPlaceholder()}
            value={displayUlbValue}
            onChange={(value) => onFilterChange('ulb_name', value)}
            allowClear={!isFilterDisabled("ulb") && !isUlbAdmin}
            style={{ width: "100%" }}
            loading={loadingFilters}
            showSearch
            disabled={isFilterDisabled("ulb") || (!displayDistrictValue && !displayDivisionValue && !hasFullAccessFn?.())}
            size="middle"
            dropdownStyle={{ borderRadius: 12 }}
          >
            <Option value="">All ULBs</Option>
            {filterOptions.ulbs?.map((value) => (
              <Option key={value} value={value}>{value}</Option>
            ))}
          </Select>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: 12, flexShrink: 0 }}>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={onApply}
            size="middle"
            style={{ background: "#ffffff", borderColor: "#ffffff", color: "#667eea", borderRadius: 10, fontWeight: 600, height: 40, padding: "0 24px" }}
          >
            Apply Filters
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={onReset}
            size="middle"
            style={{ background: "rgba(255,255,255,0.2)", borderColor: "rgba(255,255,255,0.3)", color: "#ffffff", borderRadius: 10, height: 40, padding: "0 20px" }}
          >
            Reset
          </Button>
        </div>
      </div>
    </Card>
  );
};

// ==================== MAIN COMPONENT ====================
const FormManager = () => {
  // State declarations
  const [forms, setForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnSearchTerm, setColumnSearchTerm] = useState("");
  const [searchingByColumn, setSearchingByColumn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formData, setFormData] = useState([]);
  const [showAllCards, setShowAllCards] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [lastAppliedFilters, setLastAppliedFilters] = useState(null);
  const [dataModalVisible, setDataModalVisible] = useState(false);
  const [dataModalPagination, setDataModalPagination] = useState({ current: 1, pageSize: 10 });
  
  // Dashboard Background Images from DB
  const [dashboardBgImages, setDashboardBgImages] = useState([]);
  const [currentBgImageIndex, setCurrentBgImageIndex] = useState(0);
  
  // Front page filters
  const [frontPageFilters, setFrontPageFilters] = useState({
    division: null,
    district: null,
    ulb_type: null,
    ulb_class: null,
    ulb_name: null,
  });
  const [frontPageFilterOptions, setFrontPageFilterOptions] = useState({
    divisions: [],
    districts: [],
    ulbTypes: [],
    ulbClasses: [],
    ulbs: [],
  });
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // User state for role-based filtering
  const [userRole, setUserRole] = useState(null);
  const [userDivisionId, setUserDivisionId] = useState(null);
  const [userDistrictId, setUserDistrictId] = useState(null);
  const [userUlbId, setUserUlbId] = useState(null);
  const [userDataLoaded, setUserDataLoaded] = useState(false);

  // Reference cache
  const [referenceCache, setReferenceCache] = useState({
    districts: {},
    divisions: {},
    ulbs: {}
  });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const hasFullAccess = () => {
    const fullAccessRoles = ["SUPER_ADMIN", "super_admin", "ADMIN", "DMA_DESK_OFFICER", "DMA_USER", "dma_user"];
    return fullAccessRoles.includes(userRole);
  };

  // Helper functions to get user location names
  const getUserDivisionName = () => {
    if (userDivisionId && referenceCache.divisions?.[userDivisionId]) {
      return referenceCache.divisions[userDivisionId].name;
    }
    return null;
  };

  const getUserDistrictName = () => {
    if (userDistrictId && referenceCache.districts?.[userDistrictId]) {
      return referenceCache.districts[userDistrictId].name;
    }
    return null;
  };

  const getUserUlbName = () => {
    if (userUlbId && referenceCache.ulbs?.[userUlbId]) {
      return referenceCache.ulbs[userUlbId].ulb_name;
    }
    return null;
  };

  // Get role-based forced filters
  const getRoleBasedFilters = () => {
    const forcedFilters = {};
    
    if (!hasFullAccess()) {
      if (userRole === "DIVISION_ADMIN" || userRole === "division_admin" || 
          userRole === "DIVISIONAL_OFFICER" || userRole === "divisional_officer" ||
          userRole === "Divisionalcommissioner") {
        const divisionName = getUserDivisionName();
        if (divisionName) forcedFilters.division = divisionName;
      }
      else if (userRole === "DISTRICT_ADMIN" || userRole === "district_admin") {
        const districtName = getUserDistrictName();
        if (districtName) forcedFilters.district = districtName;
        const divisionName = getUserDivisionName();
        if (divisionName) forcedFilters.division = divisionName;
      }
      else if (userRole === "ULB_ADMIN" || userRole === "ulb_admin" ||
               userRole === "ULB_OFFICER" || userRole === "ULB_CHIEF_OFFICIER") {
        const ulbName = getUserUlbName();
        if (ulbName) forcedFilters.ulb_name = ulbName;
        const districtName = getUserDistrictName();
        if (districtName) forcedFilters.district = districtName;
        const divisionName = getUserDivisionName();
        if (divisionName) forcedFilters.division = divisionName;
      }
    }
    
    return forcedFilters;
  };

  // Load dashboard background images from DB
  const loadDashboardBackgroundImages = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/dashboard-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.dashboardConfig) {
        const images = response.data.dashboardConfig.heroImages || [];
        setDashboardBgImages(images);
        console.log('🖼️ Loaded background images from DB:', images.length);
        if (images.length > 0) {
          setCurrentBgImageIndex(0);
        }
      }
    } catch (error) {
      console.error('Error loading dashboard background images:', error);
      setDashboardBgImages([]);
    }
  };

  // Auto-slideshow for background images
  useEffect(() => {
    if (dashboardBgImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentBgImageIndex((prev) => (prev + 1) % dashboardBgImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [dashboardBgImages.length]);

  // Load reference data
  const loadReferenceData = async () => {
    try {
      const token = sessionStorage.getItem("token");
      
      const [divisionsRes, districtsRes, ulbsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/references/divisions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/api/references/districts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/api/references/ulbs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const divisionsMap = {};
      divisionsRes.data.data?.forEach(div => { divisionsMap[div.id] = div; });
      
      const districtsMap = {};
      districtsRes.data.data?.forEach(dist => { districtsMap[dist.id] = dist; });
      
      const ulbsMap = {};
      ulbsRes.data.data?.forEach(ulb => { ulbsMap[ulb.id] = ulb; });

      setReferenceCache({ divisions: divisionsMap, districts: districtsMap, ulbs: ulbsMap });
      
      // Also set filter options based on reference data
      setFrontPageFilterOptions({
        divisions: [...new Set(Object.values(divisionsMap).map(d => d.name))].sort(),
        districts: [...new Set(Object.values(districtsMap).map(d => d.name))].sort(),
        ulbTypes: [...new Set(Object.values(ulbsMap).map(u => u.ulb_type).filter(Boolean))].sort(),
        ulbClasses: [...new Set(Object.values(ulbsMap).map(u => u.class).filter(Boolean))].sort(),
        ulbs: Object.values(ulbsMap).map(u => u.ulb_name).sort(),
      });
      
      // After loading reference data, apply role-based filters
      const forcedFilters = getRoleBasedFilters();
      if (Object.keys(forcedFilters).length > 0) {
        setFrontPageFilters(prev => ({ ...prev, ...forcedFilters }));
        setIsFilterApplied(true);
        loadForms(forcedFilters);
      }
    } catch (error) {
      console.error("Error loading reference data:", error);
    }
  };

  // Get current user with role and location IDs
  const getCurrentUser = () => {
    try {
      const storedUser = sessionStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        setUserRole(user.role_name || user.role || user.userRole);
        setUserDivisionId(user.division_id || user.divisionId || user.division);
        setUserDistrictId(user.district_id || user.districtId || user.district);
        setUserUlbId(user.ulb_id || user.ulbId || user.ulb);
        setUserDataLoaded(true);
        console.log("👤 User loaded:", { 
          role: user.role_name, 
          divisionId: user.division_id,
          districtId: user.district_id,
          ulbId: user.ulb_id
        });
      }
    } catch (error) {
      console.error("Error loading user:", error);
      setUserDataLoaded(true);
    }
  };

  // Load front page filter options
  const loadFrontPageFilterOptions = async () => {
    setLoadingFilters(true);
    await loadReferenceData();
    setLoadingFilters(false);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    // Don't allow changing role-based forced filters
    const forcedFilters = getRoleBasedFilters();
    if (forcedFilters[filterType]) return;
    
    setFrontPageFilters(prev => ({ ...prev, [filterType]: value }));
    if (filterType === 'division') {
      setFrontPageFilters(prev => ({ ...prev, district: null, ulb_name: null }));
    }
    if (filterType === 'district') {
      setFrontPageFilters(prev => ({ ...prev, ulb_name: null }));
    }
  };

  // Apply front page filters
  const applyFrontPageFilters = async () => {
    setIsFilterApplied(true);
    const forcedFilters = getRoleBasedFilters();
    const filtersToApply = { ...frontPageFilters, ...forcedFilters };
    await loadForms(filtersToApply);
  };

  // Reset front page filters (but keep role-based forced filters)
  const resetFrontPageFilters = async () => {
    const forcedFilters = getRoleBasedFilters();
    setFrontPageFilters({
      division: forcedFilters.division || null,
      district: forcedFilters.district || null,
      ulb_type: null,
      ulb_class: null,
      ulb_name: forcedFilters.ulb_name || null,
    });
    setIsFilterApplied(Object.keys(forcedFilters).length > 0);
    await loadForms(forcedFilters);
  };

  // HANDLE SEARCH (Name Search)
  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setFilteredForms(forms);
    } else {
      const filtered = forms.filter(
        (form) => (form.displayName || form.name || form.tableName || "").toLowerCase().includes(value.toLowerCase())
      );
      setFilteredForms(filtered);
    }
    setShowAllCards(false);
  };

  // HANDLE COLUMN SEARCH
  const handleColumnSearch = async (value) => {
    setColumnSearchTerm(value);
    setSearchingByColumn(true);

    if (!value.trim()) {
      if (searchTerm.trim()) {
        const filtered = forms.filter(
          (form) => (form.displayName || form.name || form.tableName || "").toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredForms(filtered);
      } else {
        setFilteredForms(forms);
      }
      setSearchingByColumn(false);
      return;
    }

    setLoading(true);
    try {
      const formsWithMatchingColumns = [];
      for (const form of forms) {
        try {
          const structureResponse = await axios.get(
            `${API_BASE_URL}/api/reports/forms/${form.id}/structure`,
            { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
          );
          if (structureResponse.data.success && structureResponse.data.fields) {
            const hasMatchingColumn = structureResponse.data.fields.some((field) => {
              const searchValue = value.toLowerCase();
              return (field.label || field.id || field.column_name || "").toLowerCase().includes(searchValue) ||
                (field.field_id || field.id || "").toLowerCase().includes(searchValue) ||
                (field.column_name || "").toLowerCase().includes(searchValue);
            });
            if (hasMatchingColumn) formsWithMatchingColumns.push(form);
          }
        } catch (error) { 
          console.error(`Error fetching structure for form ${form.id}:`, error); 
        }
      }
      let finalFiltered = formsWithMatchingColumns;
      if (searchTerm.trim()) {
        finalFiltered = formsWithMatchingColumns.filter(
          (form) => (form.displayName || form.name || form.tableName || "").toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      setFilteredForms(finalFiltered);
      setShowAllCards(false);
      if (finalFiltered.length === 0) {
        notification.info({ 
          message: "No Results Found", 
          description: `No forms contain a column matching "${value}"`, 
          duration: 3 
        });
      }
    } catch (error) {
      console.error("Error searching columns:", error);
      message.error("Error searching columns. Please try again.");
    } finally {
      setLoading(false);
      setSearchingByColumn(false);
    }
  };

  // HANDLE FORM SELECT
  const handleFormSelect = async (form) => {
    setSelectedForm(form);
  };

  // ========== Process report calculations for important fields ==========
  const processReportCalculations = (data, reportFields) => {
    if (!data || data.length === 0 || !reportFields || reportFields.length === 0) return {};

    const calculations = {};
    reportFields.forEach((field) => {
      const actualColumnName = field.column_name || field.field_id;
      const operator = field.operator || "sum";

      const values = data
        .map((row) => {
          const val = row[actualColumnName];
          if (val === undefined || val === null || val === "") return null;
          const strVal = String(val).replace(/,/g, "");
          const numVal = parseFloat(strVal);
          return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
        })
        .filter((v) => v !== null);

      let result = 0;
      if (values.length > 0) {
        switch (operator.toLowerCase()) {
          case "sum":
            result = values.reduce((a, b) => a + b, 0);
            break;
          case "avg":
          case "average":
            result = values.reduce((a, b) => a + b, 0) / values.length;
            break;
          case "min":
            result = Math.min(...values);
            break;
          case "max":
            result = Math.max(...values);
            break;
          case "count":
            result = values.length;
            break;
          default:
            result = values.reduce((a, b) => a + b, 0);
        }
      }

      calculations[field.field_id] = {
        value: result,
        operator,
        count: values.length,
        display_name: field.display_name || field.field_id,
      };
    });
    return calculations;
  };

  // ========== Fetch report-enabled (important) fields ==========
  const fetchReportEnabledFields = async (formId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/schema?report_enabled=true`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );
      if (response.data.success && response.data.reportEnabledFields) {
        return response.data.reportEnabledFields;
      }
      return [];
    } catch (error) {
      console.error("Error fetching report-enabled fields:", error);
      return [];
    }
  };

  // LOAD FORMS with role-based filtering
  const loadForms = async (appliedFilters = null) => {
    // Merge role-based forced filters with user-applied filters
    const forcedFilters = getRoleBasedFilters();
    const filtersToUse = { ...(appliedFilters || {}), ...forcedFilters };
    const shouldApplyFilters = Object.keys(filtersToUse).some(key => filtersToUse[key]);
    const filtersKey = JSON.stringify(filtersToUse);
    
    if (isApplyingFilters && lastAppliedFilters === filtersKey) {
      console.log("⚠️ Skipping duplicate loadForms call");
      return;
    }

    try {
      setIsApplyingFilters(true);
      setLastAppliedFilters(filtersKey);
      setLoading(true);

      let approvedFormsMap = new Map();
      try {
        const approvalResponse = await axios.get(`${API_BASE_URL}/api/reports/forms/approval-status/all`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
        });
        if (approvalResponse.data.success && approvalResponse.data.forms) {
          approvalResponse.data.forms.forEach(form => {
            approvedFormsMap.set(form.id, form.is_approved === true || form.is_approved === 1);
          });
        }
      } catch (approvalError) { 
        console.error("Error fetching approval status:", approvalError); 
      }

      const response = await axios.get(`${API_BASE_URL}/api/reports/forms`, {
        timeout: 500000,
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
      });

      if (response.data.success) {
        let formsData = response.data.forms || [];
        formsData = formsData.filter((form) => parseInt(form.id) !== 100);
        
        const approvedFormsData = formsData.filter(form => {
          const formId = parseInt(form.id);
          return approvedFormsMap.get(formId) === true;
        });

        console.log(`📋 Processing ${approvedFormsData.length} approved forms with filters:`, filtersToUse);

        const enhancedForms = await Promise.all(
          approvedFormsData.map(async (form) => {
            try {
              const params = new URLSearchParams({ limit: "10000", all_fields: "true" });
              
              // Apply role-based filters to API call
              if (filtersToUse.division) params.set("division", filtersToUse.division);
              if (filtersToUse.district) params.set("district", filtersToUse.district);
              if (filtersToUse.ulb_type) params.set("ulb_type", filtersToUse.ulb_type);
              if (filtersToUse.ulb_class) params.set("ulb_class", filtersToUse.ulb_class);
              if (filtersToUse.ulb_name) params.set("ulb_name", filtersToUse.ulb_name);
              
              const dataResponse = await axios.get(`${API_BASE_URL}/api/reports/forms/${form.id}/data?${params}`, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
              }).catch(() => ({ data: { success: false, data: [] } }));

              if (dataResponse.data.success && dataResponse.data.data.length > 0) {
                let data = dataResponse.data.data;
                data = resolveIdsInData(data, referenceCache);
                
                // Filter to approved submissions only
                const approvedSubmissions = getApprovedSubmissionsOnly(form.id, data);
                if (approvedSubmissions.length === 0) return null;

                // Fetch report-enabled fields (important fields)
                const reportFields = await fetchReportEnabledFields(form.id);
                
                let reportFieldsWithValues = [];
                
                if (reportFields.length > 0) {
                  // Calculate values for each report-enabled field
                  const calculations = processReportCalculations(approvedSubmissions, reportFields);
                  
                  // Build report fields array with values - ONLY important fields
                  reportFieldsWithValues = reportFields
                    .filter(field => field.report_important === true)
                    .map(field => {
                      const calc = calculations[field.field_id];
                      return {
                        display_name: field.display_name || field.field_id,
                        operator: field.operator || "sum",
                        value: calc ? calc.value : 0,
                        column_name: field.column_name || field.field_id,
                        field_id: field.field_id,
                      };
                    })
                    .slice(0, 6);
                }

                return { 
                  ...form, 
                  displayName: form.name || form.formName || form.tableName || `Form ${form.id}`, 
                  recordCount: approvedSubmissions.length,
                  reportFields: reportFieldsWithValues,
                  form_icon: form.form_icon,
                  form_icon_emoji: form.form_icon_emoji,
                };
              }
              return null;
            } catch (error) { 
              console.error(`Error processing form ${form.id}:`, error); 
              return null; 
            }
          })
        );

        const validForms = enhancedForms.filter(form => form !== null);
        const sortedForms = validForms.sort((a, b) => parseInt(a.id) - parseInt(b.id));
        setForms(sortedForms);
        setFilteredForms(sortedForms);
        
        console.log(`✅ Loaded ${sortedForms.length} forms with role-based filters`);
        
        if (sortedForms.length === 0) {
          const roleText = !hasFullAccess() ? 
            (userRole === "DIVISION_ADMIN" ? "your division" :
             userRole === "DISTRICT_ADMIN" ? "your district" :
             userRole === "ULB_ADMIN" ? "your ULB" : "your location") : "";
          notification.warning({
            message: "No Forms Available",
            description: `No approved forms with data found for ${roleText}.`,
            duration: 4
          });
        }
      }
    } catch (error) {
      console.error("Error loading forms:", error);
      message.error("Failed to load forms");
    } finally {
      setLoading(false);
      setIsApplyingFilters(false);
    }
  };

  // ==================== COMPACT SEARCH COMPONENT ====================
  const renderCompactSearch = () => {
    return (
      <div
        style={{
          marginBottom: "18px",
          padding: "8px 12px",
          background: "rgba(255, 255, 255, 0.95)",
          borderRadius: "10px",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Search
          placeholder="Search by form name or column..."
          allowClear
          size="middle"
          onChange={(e) => {
            const value = e.target.value;
            setColumnSearchTerm(value);
            handleColumnSearch(value);
          }}
          loading={searchingByColumn}
          style={{ width: "100%" }}
          value={columnSearchTerm}
          enterButton={
            <Button
              type="primary"
              icon={<SearchOutlined />}
              style={{
                backgroundColor: "#1890ff",
                borderColor: "#1890ff",
              }}
            />
          }
        />
      </div>
    );
  };

  // ==================== COMPACT CARD RENDERER ====================
  const renderCompactCard = (form, index) => {
    const startColor = startColors[index % startColors.length];
    const endColor = endColors[index % endColors.length];
    const hoverColor = hoverColors[index % hoverColors.length];

    const gradient = `linear-gradient(135deg, ${startColor} 0%, ${endColor} 100%)`;
    const hoverGradient = `linear-gradient(135deg, ${hoverColor} 0%, ${endColor} 100%)`;

    return (
      <div
        onClick={() => handleFormSelect(form)}
        className="radiant-card"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 12px",
          marginBottom: "10px",
          borderRadius: "12px",
          cursor: "pointer",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          background: gradient,
          border: "1px solid rgba(255,255,255,0.35)",
          boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = hoverGradient;
          e.currentTarget.style.transform = "translateX(8px) scale(1.02)";
          e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.25)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = gradient;
          e.currentTarget.style.transform = "translateX(0) scale(1)";
          e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.15)";
        }}
      >
        <div style={{ flexShrink: 0, width: "36px", textAlign: "center" }}>
          <DatabaseOutlined style={{ fontSize: "20px", color: "#ffffff", filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.15))" }} />
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#ffffff",
              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
              lineHeight: 1.3,
              letterSpacing: "0.3px",
            }}
          >
            {form.displayName || form.tableName}
          </div>
        </div>
      </div>
    );
  };

  // ==================== NORMAL CARD RENDERER - ONLY IMPORTANT FIELDS ====================
  const renderNormalCard = (form, index) => {
    const reportFields = form.reportFields || [];

    const hasImageIcon = form.form_icon_type === 'image' && form.form_icon;
    const hasEmojiIcon = form.form_icon_type === 'emoji' && form.form_icon_emoji;
    const hasNoIcon = form.form_icon_type === 'none';

    const startColor = startColors[index % startColors.length];
    const endColor = endColors[index % endColors.length];
    const hoverColor = hoverColors[index % hoverColors.length];

    const gradient = `linear-gradient(135deg, ${startColor} 0%, ${endColor} 100%)`;
    const hoverGradient = `linear-gradient(135deg, ${hoverColor} 0%, ${endColor} 100%)`;

    return (
      <Card
        hoverable
        className={`radiant-card card-cross-fade`}
        bodyStyle={{
          padding: "0 !important",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
        style={{
          borderRadius: "14px",
          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
          transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          background: gradient,
          border: "1px solid rgba(255,255,255,0.4)",
          overflow: "hidden",
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          height: "300px",
          position: "relative",
          cursor: "pointer",
        }}
        onClick={() => handleFormSelect(form, index)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = hoverGradient;
          e.currentTarget.style.transform = "translateY(-5px)";
          e.currentTarget.style.boxShadow = "0 15px 35px rgba(0,0,0,0.25)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = gradient;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
        }}
      >
        <div style={{ padding: "12px 12px 0 12px", flexShrink: 0 }}>
          <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: hasNoIcon ? "0px" : "12px" }}>
            {hasImageIcon && (
              <img
                src={form.form_icon}
                alt={form.displayName || form.tableName}
                style={{
                  width: "38px",
                  height: "38px",
                  objectFit: "cover",
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.25)",
                  padding: "4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            {hasEmojiIcon && (
              <span style={{ fontSize: "36px", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }}>
                {form.form_icon_emoji}
              </span>
            )}
            <div
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#ffffff",
                textShadow: "0 2px 4px rgba(0,0,0,0.25)",
                lineHeight: 1.2,
                transition: "all 0.3s ease",
                wordBreak: "break-word",
                flex: 1,
              }}
              onMouseEnter={(e) => (e.target.style.transform = "translateX(3px)")}
              onMouseLeave={(e) => (e.target.style.transform = "translateX(0)")}
            >
              {form.displayName || form.tableName}
            </div>
          </div>
        </div>

        <div
          style={{
            flex: "1 1 auto",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "0 12px",
            scrollbarWidth: "thin",
          }}
        >
          {reportFields.length > 0 ? (
            <div style={{ marginBottom: "0", paddingBottom: "0" }}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                {reportFields.map((field, idx) => {
                  const isLastItem = idx === reportFields.length - 1;
                  const totalItems = reportFields.length;
                  const isOddTotal = totalItems % 2 !== 0;
                  const shouldTakeFullWidth = isLastItem && isOddTotal;

                  return (
                    <div
                      key={idx}
                      style={{
                        flex: shouldTakeFullWidth ? "1 1 100%" : "1 1 calc(50% - 4px)",
                        padding: "10px 8px",
                        background: "rgba(255,255,255,0.95)",
                        borderRadius: "10px",
                        borderLeft: `3px solid rgba(255,255,255,0.8)`,
                        transition: "all 0.3s ease",
                        minHeight: "auto",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        backdropFilter: "blur(4px)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
                        e.currentTarget.style.background = "#ffffff";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0) scale(1)";
                        e.currentTarget.style.background = "rgba(255,255,255,0.95)";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#333",
                          marginBottom: "6px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Tooltip title={`Operator: ${field.operator}`}>
                          <span style={{ fontSize: "11px", color: "#666" }}>
                            {getOperatorIcon(field.operator)}
                          </span>
                        </Tooltip>
                        {field.display_name}
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: 700,
                          color: "#222",
                          textShadow: "none",
                          lineHeight: 1.2,
                        }}
                      >
                        {formatValueLocal(field.value, field.operator)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "20px", color: "#8c8c8c" }}>
              <DatabaseOutlined style={{ fontSize: "24px", marginBottom: "8px" }} />
              <div>No important data points</div>
            </div>
          )}
        </div>

        <div
          style={{
            padding: "10px 12px",
            borderTop: "1px solid rgba(255,255,255,0.3)",
            background: "transparent",
            flexShrink: 0,
            marginBottom: 0,
          }}
        >
          <Button
            block
            size="small"
            type="primary"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleFormSelect(form);
            }}
            style={{
              fontSize: "12px",
              height: "34px",
              borderRadius: "10px",
              fontWeight: 600,
              marginBottom: 0,
              backgroundColor: "#ffffff",
              borderColor: "#ffffff",
              color: startColor,
              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.15)";
            }}
          >
            Graphical View
          </Button>
        </div>
      </Card>
    );
  };

  // ==================== NORMAL CARDS GRID ====================
  const renderNormalCardsGrid = () => {
    if (filteredForms.length === 0) {
      return (
        <Empty 
          description="No forms found. Try adjusting your search or filters." 
          style={{ padding: "60px 0" }} 
        />
      );
    }

    return (
      <Row gutter={[16, 16]}>
        {filteredForms.map((form, index) => (
          <Col xs={24} sm={12} md={8} lg={6} key={form.id}>
            {renderNormalCard(form, index)}
          </Col>
        ))}
      </Row>
    );
  };

  // ==================== HERO SECTION WITH BACKGROUND IMAGES ====================
  const renderHeroSection = () => {
    if (dashboardBgImages.length === 0) return null;

    const currentImage = dashboardBgImages[currentBgImageIndex];

    return (
      <div
        style={{
          position: "relative",
          width: isMobile ? "100%" : "100vw",
          left: isMobile ? "0" : "50%",
          right: isMobile ? "0" : "50%",
          marginLeft: isMobile ? "0" : "-50vw",
          marginRight: isMobile ? "0" : "-50vw",
          minHeight: isMobile ? "400px" : "600px",
          marginBottom: isMobile ? "30px" : "50px",
          borderRadius: isMobile ? "12px" : "18px",
          overflow: "hidden",
        }}
      >
        {/* Background Image with Carousel */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${currentImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            transition: "opacity 1s ease-in-out",
          }}
        />
        
        {/* Overlay for better text readability */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)",
            zIndex: 1,
          }}
        />

        {/* Carousel Dots */}
        {dashboardBgImages.length > 1 && (
          <div
            style={{
              position: "absolute",
              bottom: "20px",
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              zIndex: 3,
            }}
          >
            {dashboardBgImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBgImageIndex(index)}
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: currentBgImageIndex === index ? "#ffffff" : "rgba(255,255,255,0.5)",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </div>
        )}

        {/* Content Container - Compact Cards on the right */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            minHeight: "620px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            padding: "30px 3% 30px 40px",
            maxWidth: "100%",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "420px",
              maxHeight: "calc(620px - 60px)",
              overflowY: "auto",
              paddingRight: "8px",
              scrollbarWidth: "thin",
            }}
          >
            {renderCompactSearch()}
            
            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
              {filteredForms.map((form, index) => (
                <div key={`hero-compact-${form.id}`} style={{ width: "100%" }}>
                  {renderCompactCard(form, index)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER DATA MODAL ====================
  const renderDataModal = () => {
    if (!selectedForm || !formData.length) return null;
    
    const paginatedData = formData.slice(
      (dataModalPagination.current - 1) * dataModalPagination.pageSize,
      dataModalPagination.current * dataModalPagination.pageSize
    );
    
    const columns = Object.keys(formData[0] || {}).slice(0, 8).map(key => ({
      title: formatFieldName(key),
      dataIndex: key,
      key: key,
      ellipsis: true,
      width: 150,
      render: (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'object') return JSON.stringify(value).substring(0, 50);
        return String(value);
      }
    }));

    return (
      <Modal
        title={
          <Space>
            <DatabaseOutlined style={{ color: "#1890ff" }} />
            <span>{selectedForm.displayName || selectedForm.name}</span>
            <Tag color="blue">{formData.length} Records</Tag>
          </Space>
        }
        open={dataModalVisible}
        onCancel={() => { 
          setDataModalVisible(false); 
          setSelectedForm(null); 
          setFormData([]); 
        }}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ padding: "16px" }}
      >
        <div style={{ overflowX: "auto" }}>
          <Table
            dataSource={paginatedData}
            columns={columns}
            rowKey={(_, index) => index}
            pagination={{
              current: dataModalPagination.current,
              pageSize: dataModalPagination.pageSize,
              total: formData.length,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
              onChange: (page, pageSize) => setDataModalPagination({ current: page, pageSize }),
            }}
            size="small"
            scroll={{ x: "max-content", y: 500 }}
          />
        </div>
      </Modal>
    );
  };

  // Handle back from form selection view
  const handleBackToForms = () => {
    setSelectedForm(null);
    setFormData([]);
    // Reload forms with role-based filters
    const forcedFilters = getRoleBasedFilters();
    loadForms(forcedFilters);
  };

  // Load data on mount
  useEffect(() => {
    getCurrentUser();
    loadReferenceData();
    loadFrontPageFilterOptions();
    loadDashboardBackgroundImages();
  }, []);

  // ==================== MAIN RENDER ====================
  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto", minHeight: "100vh", background: "#f8f9fa" }}>
      
      {/* If a form is selected, show only FormSelectionView in full page */}
      {selectedForm ? (
        <FormSelectionView 
          form={selectedForm} 
          onBack={handleBackToForms}
          referenceCache={referenceCache}
          userRole={userRole}
          userDivisionId={userDivisionId}
          userDistrictId={userDistrictId}
          userUlbId={userUlbId}
          hasFullAccessFn={hasFullAccess}
        />
      ) : (
        <>
          {/* Hero Section with Background Images and Compact Cards */}
          {dashboardBgImages.length > 0 && renderHeroSection()}

          {/* Front Page Filter Section */}
          <FrontPageFilterSection
            frontPageFilters={frontPageFilters}
            onFilterChange={handleFilterChange}
            onApply={applyFrontPageFilters}
            onReset={resetFrontPageFilters}
            filterOptions={frontPageFilterOptions}
            loadingFilters={loadingFilters}
            hasFullAccessFn={hasFullAccess}
            userRole={userRole}
            userDivisionId={userDivisionId}
            userDistrictId={userDistrictId}
            userUlbId={userUlbId}
            referenceCache={referenceCache}
          />

          {/* Stats */}
          <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
            {searchTerm && (
              <Tag closable onClose={() => handleSearch("")} color="orange" style={{ padding: "4px 12px", borderRadius: 20 }}>
                Search: "{searchTerm}"
              </Tag>
            )}
            {columnSearchTerm && (
              <Tag closable onClose={() => handleColumnSearch("")} color="orange" style={{ padding: "4px 12px", borderRadius: 20 }}>
                Column: "{columnSearchTerm}"
              </Tag>
            )}
            {isFilterApplied && (frontPageFilters.division || frontPageFilters.district || frontPageFilters.ulb_type) && (
              <Tag icon={<CheckCircleOutlined />} color="success" style={{ padding: "4px 12px", borderRadius: 20 }}>
                Filters Applied
              </Tag>
            )}
          </div>

          {/* Loading State */}
          {loading && !selectedForm && (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin size="large" />
              <div style={{ marginTop: 20, color: "#8c8c8c" }}>Loading forms...</div>
            </div>
          )}

          {/* Compact Cards Section (if no hero images) */}
          {!loading && dashboardBgImages.length === 0 && (
            <div style={{ marginBottom: 24 }}>
              <Title level={4}>Quick Access</Title>
              <Row gutter={[16, 16]}>
                {filteredForms.slice(0, 6).map((form, index) => (
                  <Col xs={12} sm={8} md={6} key={`quick-${form.id}`}>
                    {renderCompactCard(form, index)}
                  </Col>
                ))}
              </Row>
            </div>
          )}
          
          {/* Normal Cards Grid */}
          {!loading && (
            <div>
              <Title level={4}>All Forms</Title>
              {renderNormalCardsGrid()}
            </div>
          )}
        </>
      )}

      {/* Data Modal */}
      {renderDataModal()}
    </div>
  );
};

export default FormManager;